import { Check, ExternalLink, Loader2, PenLine, Users, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateEsign } from '../../api/useCreateEsign';
import { useEsignStatus } from '../../api/useEsignStatus';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';

/**
 * The applicant's view of an eSign Document Template step.
 *
 * Generating the document and raising the signature request are BILLED, and they fire
 * on arrival rather than behind a button: the step before this one is always a Document
 * Template whose button reads "Proceed for eSign", so consent has already been given and
 * a second confirmation here would be a dead click in front of what was just asked for.
 *
 * The applicant then signs on SignCare's own page, embedded here. That page owns the
 * OTP, the Aadhaar redirect and the certificate, none of which can be reproduced;
 * embedding keeps the applicant inside the session instead of handing them off to
 * another site and hoping they come back.
 *
 * The step stays PENDING until EVERY signer is done, so this screen is also where the
 * applicant waits out the counter-signatories. State comes from the server rather than
 * from local state, so a refresh — or coming back tomorrow — resumes where they were.
 */
export function EsignStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useCreateEsign(session.id, step.id);
  const [expired, setExpired] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  // Server-owned: whether a request exists, where to sign, and how far it has got.
  // Local state would be lost on refresh and re-show the (billed) send button.
  const { data: status, isLoading, refetch } = useEsignStatus(session.id, step.id);

  // useCallback so the auto-send effect below has a stable dependency: mutate and
  // refetch are already stable, so this identity never changes and the effect can list
  // it honestly instead of suppressing the exhaustive-deps rule.
  const handleSend = useCallback(() => {
    setSendFailed(false);
    mutate(undefined, {
      onSuccess: () => void refetch(),
      onError: (error) => {
        if (isSessionExpiredError(error)) {
          setExpired(true);
          return;
        }
        // No toast here. queryClient's MutationCache already raises one for every failed
        // mutation, and phrases it better (getApiErrorMessage maps the API's code/detail
        // to friendly copy). A second one on top is the duplicate-notification bug.
        setSendFailed(true);
      },
    });
  }, [mutate, refetch]);

  // Raise the request on arrival rather than behind another button.
  //
  // The applicant has already consented: the step before this one is always a Document
  // Template, and its button reads "Proceed for eSign" — asking them to confirm again on
  // the very next screen is a dead click in front of the thing they just asked for.
  //
  // Safe to fire from an effect even though the call is BILLED: the handler is
  // idempotent on the step's stored result, so a re-run (StrictMode, a remount, a
  // double render) returns the links already issued instead of buying a second document.
  // The ref only avoids the wasted round trip.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!status || status.issued || autoSentRef.current || isPending) return;

    autoSentRef.current = true;
    handleSend();
  }, [status, isPending, handleSend]);

  if (expired) {
    return <SessionExpiredScreen />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-[#9CA3AF]" />
      </div>
    );
  }

  // Auto-send in flight — the applicant asked for this on the previous screen, so the
  // honest thing to show is the work happening, not a button repeating the question.
  if (!status?.issued && !sendFailed) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
        <Loader2 size={24} className="animate-spin text-primary" />
        <p className="text-[length:var(--text-14)] font-medium text-[#041A3A]">
          Preparing your document…
        </p>
        <p className="max-w-xs text-xs leading-relaxed text-[#666666]">
          This takes a few seconds. You'll be able to sign as soon as it's ready.
        </p>
      </div>
    );
  }

  // Someone refused. Terminal — the step is already Failed server-side.
  if (status?.rejectReason) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FEE2E2]">
          <XCircle size={24} className="text-[#BF3636]" />
        </div>
        <h3 className="text-base font-semibold text-[#041A3A]">Signing was declined</h3>
        <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
          {status.rejectReason}
        </p>
      </div>
    );
  }

  // The applicant has signed. Their part is over whatever anyone else does, so this
  // says so plainly and lets them leave — the webhook finishes the session without them.
  if (status?.applicantSigned) {
    const waitingOn = Math.max(status.signerCount - status.signedCount, 0);

    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#DCFCE7]">
          <Check size={24} className="text-[#166534]" />
        </div>
        <h3 className="text-base font-semibold text-[#041A3A]">Your signature is recorded</h3>

        {waitingOn > 0 ? (
          <>
            <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
              We're now waiting for{' '}
              {waitingOn === 1 ? 'one more signer' : `${waitingOn} more signers`}. You can close
              this page — we'll email you the signed copy once everyone is done.
            </p>
            <p className="flex items-center gap-2 rounded-full bg-[#F1F5FB] px-3 py-1.5 text-xs font-medium text-[#041A3A]">
              <Users size={14} aria-hidden="true" />
              {status.signedCount} of {status.signerCount} signed
            </p>
          </>
        ) : (
          <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
            Everything is signed. The signed document is on its way to you.
          </p>
        )}
      </div>
    );
  }

  // Request raised and it's their turn — SignCare's page, inline.
  //
  // `flex-1 min-h-0` on both the wrapper and the frame: the card this renders in is a
  // full-height flex column, so the frame takes every pixel that's left instead of
  // sitting in a fixed box. A short box made the signing page scroll INSIDE the panel
  // that was already scrolling — two scrollbars for one screen of content.
  if (status?.issued && status.applicantSignLink) {
    return (
      <div className="-mx-2 flex min-h-0 flex-1 flex-col gap-1.5">
        <div className="flex shrink-0 items-center justify-between gap-3 px-2">
          <p className="text-[length:var(--text-14)] font-medium text-[#041A3A]">
            Sign your document
          </p>
          <a
            href={status.applicantSignLink}
            target="_blank"
            rel="noopener noreferrer"
            title="Trouble loading? Open the signing page in its own tab."
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-primary transition hover:bg-primary/10"
          >
            <ExternalLink size={14} aria-hidden="true" />
            New tab
          </a>
        </div>

        <iframe
          src={status.applicantSignLink}
          title="Sign your document"
          // allow-same-origin is required: the signing page needs its own cookies and
          // storage to hold the OTP step. Without it every attempt would start over.
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
          allow="camera; geolocation"
          className="min-h-0 w-full flex-1 rounded-xl border border-[#E5E7EB] bg-white"
        />
      </div>
    );
  }

  // Raised, but this person isn't a signer — they're only waiting on others.
  if (status?.issued) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Users size={24} className="text-primary" />
        </div>
        <h3 className="text-base font-semibold text-[#041A3A]">Waiting for signatures</h3>
        <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
          The document has gone out for signature. You can close this page — we'll email you once
          it's signed.
        </p>
        <p className="flex items-center gap-2 rounded-full bg-[#F1F5FB] px-3 py-1.5 text-xs font-medium text-[#041A3A]">
          <Users size={14} aria-hidden="true" />
          {status.signedCount} of {status.signerCount} signed
        </p>
      </div>
    );
  }

  // Only reached when preparing the document failed. Everything above is the happy
  // path, so this is a retry — not the normal way in.
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FEE2E2]">
        <XCircle size={24} className="text-[#BF3636]" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-[#041A3A]">
          We couldn't prepare your document
        </h3>
        <p className="mx-auto mt-1 max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
          Nothing has been sent yet. Try again — if it keeps failing, contact whoever sent you this
          link.
        </p>
      </div>

      <button
        type="button"
        onClick={handleSend}
        disabled={isPending}
        className="flex w-full max-w-xs items-center justify-center gap-2 rounded-xl bg-primary py-3 text-[length:var(--text-14)] font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isPending ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />}
        {isPending ? 'Preparing your document…' : 'Try again'}
      </button>
    </div>
  );
}
